import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionMargins,
  sectionPaddings,
  standardColours,
} from '../styles';
import { ArrowLink, Button, Container, Heading, Tag } from './ui';
import { GatsbyImage } from 'gatsby-plugin-image';

const StyledFeaturedResource = styled.article`
  ${({ isCaseStudyDetail }) => {
    if (!isCaseStudyDetail) {
      return css`
        ${sectionMargins('45px', '90px')};
      `;
    } else {
      return css`
        ${sectionMargins('30px', '60px')};
      `;
    }
  }}
`;

const StyledContainer = styled(Container)`
  ${maxBreakpointQuery.tiny`
    padding-left: 0;
    padding-right: 0;
  `}
`;

const StyledInner = styled.div`
  ${sectionPaddings('40px', '90px')};
  background-color: ${brandColours.primary};
  border-radius: 50px;
`;

const StyledHeading = styled(Heading)`
  text-align: center;
  margin-bottom: 40px;

  ${minBreakpointQuery.small`
    margin-bottom: 50px;
  `}

  ${minBreakpointQuery.medium`
    margin-bottom: 60px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 70px;
  `}
`;

const StyledContent = styled.div`
  display: grid;
  border-radius: 20px;
  overflow: hidden;
  background-color: ${standardColours.white};

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
  `}
`;

const StyledBottom = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 30px;

  ${minBreakpointQuery.medium`
    padding: 30px 50px;
  `}

  ${minBreakpointQuery.large`
    padding: 45px 70px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding: 60px 90px;
  `}
`;
const StyledImageWrapper = styled.div`
  padding: 10px;
  background-color: ${brandColours.octonary};
  display: flex;
  align-items: center;

  ${minBreakpointQuery.tsmall`
    padding: 20px;
  `}

  ${minBreakpointQuery.small`
    padding: 30px;
  `}

  ${minBreakpointQuery.smedium`
    padding: 10px;
  `}

  ${minBreakpointQuery.medium`
    padding: 20px;
  `}

  ${minBreakpointQuery.mlarge`
    padding: 30px;
  `}
`;

const StyledImage = styled(GatsbyImage)`
  width: 100%;
`;

const StyledTag = styled(Tag)`
  margin-right: auto;
  background-color: ${brandColours.tertiary};
  border-color: ${brandColours.tertiary};
  border-radius: 10px;
  padding: 4px 18px;
`;

const StyledResourceHeading = styled.h3`
  ${fontSize(21)};
  line-height: 1.4;
  font-weight: ${fontWeights.semibold};
  color: ${brandColours.secondary};

  ${minBreakpointQuery.small`
    ${fontSize(23)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(25)};
  `};
`;

const StyledText = styled.p`
  line-height: 1.7;
  color: ${brandColours.secondary};
`;

const StyledButton = styled(Button)`
  margin-top: auto;
  color: ${brandColours.secondary};
  border-radius: 45px;
  ${fontSize(12)};

  ${minBreakpointQuery.mlarge`
    ${fontSize(14)};
  `};
`;

const StyledLink = styled(ArrowLink)`
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}
`;

const FeaturedResource = ({
  heading,
  resource: { ctaImage, platform, title, summary, link, ctaButtonText },
  isCaseStudyDetail,
  isBlogDetail,
}) => {
  const {
    datoCmsResourceListing: { slug },
  } = useStaticQuery(graphql`
    query FeaturedResourceQuery {
      datoCmsResourceListing {
        slug
      }
    }
  `);

  return (
    <StyledFeaturedResource isCaseStudyDetail={isCaseStudyDetail}>
      <StyledContainer wider={true}>
        <StyledInner>
          <Container>
            <StyledHeading>{heading}</StyledHeading>
            <StyledContent>
              <StyledImageWrapper>
                <StyledImage
                  image={ctaImage.gatsbyImageData}
                  objectFit="contain"
                  alt={ctaImage.alt}
                />
              </StyledImageWrapper>
              <StyledBottom>
                {platform && <StyledTag>{platform.title}</StyledTag>}
                <StyledResourceHeading>{title}</StyledResourceHeading>
                <StyledText>{summary}</StyledText>
                <StyledButton
                  to={link.pageUrl}
                  {...(isBlogDetail && { title: link.text })}
                >
                  {isBlogDetail ? ctaButtonText || 'Download now' : link.text}
                </StyledButton>
              </StyledBottom>
            </StyledContent>
            <StyledLink to={slug}>View all resources</StyledLink>
          </Container>
        </StyledInner>
      </StyledContainer>
    </StyledFeaturedResource>
  );
};

export default FeaturedResource;

export const FeaturedResourceFragment = graphql`
  fragment FeaturedResourceFragment on Node {
    ... on DatoCmsEbook {
      ...EbookCardFragment
      ctaHeading
      ctaText
      ctaKeyPoints
      ctaImage {
        gatsbyImageData(width: 500)
        alt
      }
    }
    ... on DatoCmsTool {
      ...ToolCardFragment
      ctaHeading
      ctaText
      ctaKeyPoints
      ctaImage {
        gatsbyImageData(width: 500)
        alt
      }
      ctaButtonText
    }
  }
`;
